import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';

import { ClientPicker } from '@/shared/clients/ClientPicker';
import { useClient } from '@/shared/clients/useClients';
import { failurePasses, failureText, retryLine } from '@/shared/emails/failureCodes';
import { useReprocess } from '@/shared/emails/useEmails';
import { AiNotice } from '@/shared/ui/AiNotice';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Spinner } from '@/shared/ui/Spinner';
import { Stepper, type Step } from '@/shared/ui/Stepper';
import { FailureCode, TechnicalDetails } from '@/shared/ui/TechnicalDetails';
import { ArrowLeftIcon, FileTextIcon } from '@/shared/ui/icons';
import { formatDate } from '@/shared/utils/format';
import {
  useCan,
  useCounterMode,
  useShowsMaxDiscount,
  useShowsPurchase,
} from '@/shared/session/profiles';

import { CopiesCard } from './CopiesCard';
import { LineEditor, type LineEditorHandle } from './LineEditor';
import { NotAnOffer } from './NotAnOffer';
import { OfferUpload } from './OfferUpload';
import { PasteList } from './PasteList';
import { PdfPreview, QuoteActions } from './QuoteActions';
import { QuoteStatusBadge } from './QuoteStatusBadge';
import { Warnings } from './Warnings';
import { editableNumber, money, percent } from './format';
import { payloadWithMargins } from './lineDrafts';
import { readingEstimateText } from './offerFiles';
import { readingProgress, useOfferReading } from './offerReading';
import { QUOTE_STATUS_LABELS, isEditable, type Quote } from './types';
import {
  useQuote,
  useRecalculate,
  useSaveLines,
  useUpdateQuote,
  useUploadOfferDocuments,
} from './useQuotes';

const dateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium' });

/**
 * The life of a quote, as the screen tells it. Approving is step 3 of 5, and the two
 * after it (the PDF, the sending) are visible before anybody presses anything.
 */
const STEPS: Step[] = [
  { key: 'lines', label: 'Revisar líneas', hint: 'Cantidades, márgenes, lo que sobra.' },
  { key: 'client', label: 'Elegir cliente', hint: 'Se copia al presupuesto al aprobar.' },
  { key: 'approve', label: 'Aprobar', hint: 'Da el número definitivo y genera el PDF.' },
  {
    key: 'pdf',
    label: 'Enviar al cliente',
    hint: 'Por correo desde aquí, o descargando el PDF.',
  },
  { key: 'sent', label: 'Enviado' },
];

function stepOf(quote: Quote): number {
  if (quote.status === 'draft') {
    if ((quote.lines?.length ?? 0) === 0) return 0;
    return quote.client_id ? 2 : 1;
  }
  if (quote.status === 'reviewed') return 3;
  return 5;
}

const HELP = [
  <>
    <strong>Coste</strong> es lo que dijo el proveedor y no se toca. <strong>Margen</strong> es lo
    tuyo: cambia el de una línea o aplica uno a todas. El <strong>precio</strong> lo calcula el
    servidor con los dos.
  </>,
  <>
    Los precios y los totales <strong>no cambian mientras escribes</strong>: cambian al pulsar
    «Guardar líneas». Así la pantalla y el PDF dicen siempre lo mismo.
  </>,
  <>
    Una línea que no venía en la oferta («Portes», un artículo suelto) se añade con «Añadir línea» y
    ahí sí se teclea el precio.
  </>,
  <>
    Las líneas <strong>de las tarifas</strong> («Añadir» en Documentos o «Pegar lista») llevan el
    PVP del proveedor y su descuento habitual: se cambia el <strong>descuento</strong>, no el
    precio. Un descuento por encima del máximo del proveedor sale en rojo.
  </>,
  <>
    <strong>Aprobar</strong> da el número definitivo, congela la ficha del cliente y genera el PDF.
    Después ya no se toca: si está mal, se anula y se rehace. Se puede aprobar sin cliente, y el
    documento sale con ese bloque en blanco.
  </>,
  <>
    <strong>Enviar por correo</strong> manda el PDF al cliente desde el buzón de la oferta, con su
    propio servidor, y deja una copia en su carpeta de Enviados. Para, copia, asunto y mensaje se
    pueden cambiar. Si lo mandas tú desde tu correo, «Marcar como enviado» solo lo anota.
  </>,
];

/**
 * The quote editor: where a supplier's offer becomes something a customer can be sent.
 *
 * Everything on this screen that is a number came computed from the API. The front does no
 * arithmetic on money at all, which is why the total here and the total on the PDF cannot
 * drift apart: they are the same stored figures.
 */
export function QuoteEditorPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const quote = useQuote(quoteId);

  if (!quoteId) {
    return (
      <EmptyState
        title="Presupuesto no encontrado"
        hint="La dirección no lleva ningún identificador."
        action={
          <Link to="/presupuestos" className="link">
            Volver al listado
          </Link>
        }
      />
    );
  }

  if (quote.isPending) return <Spinner variant="block" label="Cargando presupuesto" />;
  if (quote.isError) {
    return <ErrorMessage error={quote.error}>No se pudo abrir el presupuesto</ErrorMessage>;
  }

  return <QuoteEditorView quote={quote.data} quoteId={quoteId} />;
}

/*
 * Layout. Desktop (`xl`, 1280 px and up): two columns, the lines first on the left and
 * a column on the right with the customer, totals and document options. The primary
 * action and total stay in a fixed bottom bar. Under `xl`, one column shows the
 * customer, lines, warnings, totals and document options. The wrappers use `contents`, so
 * every block is a child of the same grid and `order-*` decides; on `xl` the wrappers
 * are the columns and the same `order-*` keeps the blocks in place inside each.
 *
 * `xl` and not `lg` because at 1024 px the left column would be 370 px wide once the
 * customer picker has taken its 20.5 rem: the table cannot fit, and a stacked page is
 * better than a table that scrolls sideways.
 */
const COLUMN = 'max-xl:contents xl:flex xl:flex-col xl:gap-5';

function QuoteEditorView({ quote, quoteId }: { quote: Quote; quoteId: string }) {
  const saveLines = useSaveLines(quoteId);
  const recalculate = useRecalculate(quoteId);
  const updateQuote = useUpdateQuote(quoteId);
  const editable = isEditable(quote);
  // Read by id and not carried inside the quote: until the approval the quote only
  // points at a customer, and the card it points at can change under it. Only while it
  // is a draft: an approved quote prints its own frozen copy and the live card is none
  // of its business.
  const client = useClient(editable ? quote.client_id : null);
  // A disabled query reports `pending` for ever in React Query 5, so "we are waiting for
  // a card" is only true when there is a card to wait for. Without this the picker would
  // show a spinner instead of the search box on every quote with no customer.
  const clientLoading = Boolean(quote.client_id) && editable && client.isPending;
  const [margin, setMargin] = useState(editableNumber(quote.default_margin_pct) || '25');
  // Lifted out of the table because the global margin depends on it: applying a margin to
  // everything while there are unsaved per line margins would be undone by the save that
  // follows, which reposts exactly the margins the global one had just cleared.
  const [dirty, setDirty] = useState(false);
  const marginReady = margin.trim() !== '';
  // Contract v10.47: the counter mode hides the cost, the margin and the supplier's data
  // while a customer looks at the screen; the person may see them and turns it off.
  const showsPurchase = useShowsPurchase();
  // Contract v10.52: the lines of the tariffs. The maximum discount marks a line that goes
  // past it, for whoever may see it; «Pegar lista» for a profile that makes them.
  const showsMaxDiscount = useShowsMaxDiscount();
  const counterMode = useCounterMode();
  const canTariff = useCan('tariff_quotes');
  const [pasting, setPasting] = useState(false);
  const fromTariff = quote.origin === 'tariff';
  // The global margin moves the lines priced from a cost; a tariff line keeps its price
  // and its discount. On a quote from the tariffs it is offered only when some line is
  // priced from a cost (a supplier with only a net price, ❓ in ADR 0046 of the back).
  const costLines = (quote.lines ?? []).some(
    (line) => line.price_source !== 'tariff' && line.net_cost != null,
  );
  const tariffLines = (quote.lines ?? []).some((line) => line.price_source === 'tariff');
  /**
   * What «Aplicar a todas las líneas» replaced, while its «Deshacer» is on offer (contract
   * v10.24). The global margin is saved at once and clears every per line margin on the
   * server, so the way back is the old default (`POST /recalculate`) and then the old per
   * line margins on the same lines (`PUT /lines`). Gone as soon as anything else changes.
   */
  const [applied, setApplied] = useState<{
    defaultMargin: string;
    margins: Record<string, string | null | undefined>;
  } | null>(null);
  const [undoingMargin, setUndoingMargin] = useState(false);

  function applyMargin(): void {
    const before = {
      defaultMargin: quote.default_margin_pct ?? '',
      margins: Object.fromEntries((quote.lines ?? []).map((line) => [line.id, line.margin_pct])),
    };
    recalculate.mutate(margin.trim().replace(',', '.'), {
      onSuccess: () => setApplied(before),
    });
  }

  async function undoMargin(): Promise<void> {
    if (!applied) return;
    setUndoingMargin(true);
    try {
      const restored = await recalculate.mutateAsync(applied.defaultMargin || undefined);
      await saveLines.mutateAsync({ lines: payloadWithMargins(restored, applied.margins) });
      setMargin(editableNumber(applied.defaultMargin) || '25');
      setApplied(null);
    } finally {
      setUndoingMargin(false);
    }
  }

  // A line edit or a new quote from the server ends the offer: undoing then would put back
  // margins on lines that are no longer the ones it replaced.
  useEffect(() => {
    if (dirty) setApplied(null);
  }, [dirty]);
  // The document behind the quote, when there is one, and where its reading stands
  // (contract v8). Polls both rows while the worker is on it, and nothing afterwards.
  const reading = useOfferReading(quote);
  const upload = useUploadOfferDocuments(quoteId);
  // Where «Elegir cliente» takes the person: the customer card, and the search box in it.
  const clientRef = useRef<HTMLDivElement>(null);
  // The line editor's own save, so «Guardar y descargar borrador» can save what is on
  // screen before asking for the PDF.
  const linesRef = useRef<LineEditorHandle>(null);

  /**
   * Why approving is not available yet, or null.
   *
   * The customer one is not politeness: choosing a customer is a PATCH, and until it
   * answers `quote.client_id` is still null. Approving in that window freezes **no**
   * customer, burns a number out of the fiscal series and renders a PDF with the
   * customer block blank, and the only way back is cancelling and starting again.
   */
  const blocked = dirty
    ? 'Hay líneas sin guardar. Guárdalas antes de aprobar.'
    : updateQuote.isPending
      ? 'Guardando el cliente. Espera un segundo.'
      : recalculate.isPending
        ? 'Actualizando los importes. Espera antes de aprobar.'
        : clientLoading
          ? 'Cargando la ficha del cliente.'
          : reading.state === 'reading'
            ? 'El documento se está leyendo. Espera a que aparezcan las líneas.'
            : null;

  function chooseClient(): void {
    const card = clientRef.current;
    if (!card) return;
    if (typeof card.scrollIntoView === 'function') {
      card.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
    card.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
  }

  // Where this draft came from, when it was redone (contract v10.7): the original's
  // number is worth a request, but the line must not wait for it.
  const source = useQuote(quote.duplicated_from_id ?? undefined);
  const redoneFrom = quote.duplicated_from_id ? (
    <>
      {source.data && source.data.client_id !== quote.client_id
        ? 'Copia de'
        : 'Rehecho a partir de'}{' '}
      <Link className="link" to={`/presupuestos/${quote.duplicated_from_id}`}>
        {source.data?.quote_number ?? 'un presupuesto anulado'}
      </Link>
    </>
  ) : null;

  const documentLink = quote.email_id ? (
    <Link className="link" to={`/correos/${quote.email_id}`}>
      {reading.manual ? 'Ver el documento' : 'Ver el correo original'}
    </Link>
  ) : null;

  // One line under the title: whose offer this is, its number and date, and the way to
  // the document. The rest of what the supplier wrote is folded below the lines.
  // In the counter mode (contract v10.47) the line says nothing of the supplier: whose offer
  // this is, its number and the way to its priced document are what Cubas pays for.
  const offerLine = fromTariff ? (
    <>{redoneFrom ? <>{redoneFrom} · </> : null}Desde las tarifas</>
  ) : !showsPurchase ? (
    (redoneFrom ?? null)
  ) : quote.supplier_name ? (
    <>
      {redoneFrom ? <>{redoneFrom} · </> : null}
      Oferta de {quote.supplier_name}
      {quote.supplier_offer_number ? ` · nº ${quote.supplier_offer_number}` : ''}
      {quote.supplier_offer_date ? ` · ${formatDate(dateFormat, quote.supplier_offer_date)}` : ''}
      {documentLink ? <> · {documentLink}</> : null}
    </>
  ) : reading.state === 'reading' ? (
    'Leyendo el documento del proveedor…'
  ) : quote.email_id ? (
    <>
      {redoneFrom ? <>{redoneFrom} · </> : null}Con un documento detrás, sin proveedor leído ·{' '}
      {documentLink}
    </>
  ) : redoneFrom ? (
    <>{redoneFrom} · hecho a mano</>
  ) : editable ? (
    'Hecho a mano, sin oferta de proveedor detrás'
  ) : (
    'Hecho a mano: no viene de ningún correo'
  );

  // The offer block goes first while it is the step in progress: nothing to upload yet,
  // or a reading in flight or failed. Once read, the lines are the offer.
  // A quote from the tariffs has no supplier's document: nothing to upload or wait for.
  const offerFirst = editable && reading.state !== 'read' && !fromTariff;
  // The offer card exists while there is something to do about the document: upload it,
  // wait for it, or retry it. Read, the supplier's name is in the header and the rest is
  // folded under the lines.
  const offerCard =
    !fromTariff &&
    !quote.supplier_name &&
    ((editable && !quote.email_id) ||
      reading.state === 'reading' ||
      reading.state === 'retrying' ||
      reading.state === 'failed' ||
      reading.state === 'unread');
  const offerDetails = Boolean(quote.supplier_valid_until || quote.payment_terms);

  return (
    <>
      <PageHeader
        back={
          <Link
            to="/presupuestos"
            className="text-ink-3 hover:text-ink inline-flex items-center gap-1.5"
          >
            <ArrowLeftIcon size={16} />
            Presupuestos
          </Link>
        }
        title={quote.quote_number ?? 'Borrador'}
        badge={editable ? null : <QuoteStatusBadge status={quote.status} />}
        description={offerLine}
        help={editable ? HELP : undefined}
      />

      <div className="border-line-2 border-b pb-3">
        <Stepper steps={STEPS} current={stepOf(quote)} cancelled={quote.status === 'cancelled'} />
      </div>

      {!editable ? (
        <Callout compact tone={quote.status === 'cancelled' ? 'neutral' : 'info'}>
          Este presupuesto está en <strong>{QUOTE_STATUS_LABELS[quote.status]}</strong> y ya no se
          puede editar: si hay que cambiarlo, se anula y se rehace.
        </Callout>
      ) : null}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_20.5rem] xl:items-start">
        <div className={COLUMN}>
          <PdfPreview quote={quote} className="order-3" />

          <LineEditor
            className={offerFirst ? 'order-4' : 'order-3'}
            quote={quote}
            disabled={!editable}
            saving={saveLines.isPending}
            error={saveLines.isError ? saveLines.error : null}
            dirty={dirty}
            onDirtyChange={setDirty}
            saveRef={linesRef}
            onSave={(payload) => saveLines.mutateAsync(payload)}
            showPurchase={showsPurchase}
            showMaxDiscount={showsMaxDiscount}
            showSupplier={!counterMode}
            actions={
              editable && canTariff ? (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<FileTextIcon size={15} />}
                  onClick={() => setPasting(true)}
                >
                  Pegar lista
                </Button>
              ) : null
            }
            toolbar={
              editable && showsPurchase && (!fromTariff || costLines) ? (
                <div className="border-line-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-4 py-2">
                  <label htmlFor="margen-global" className="text-ink-2 text-[13px] font-medium">
                    Margen global (%)
                  </label>
                  <input
                    id="margen-global"
                    className="field num h-8 w-16 px-1.5 text-[13.5px]"
                    inputMode="decimal"
                    value={margin}
                    onChange={(event) => setMargin(event.target.value)}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={applyMargin}
                    disabled={recalculate.isPending || !marginReady || dirty || undoingMargin}
                  >
                    {recalculate.isPending && !undoingMargin
                      ? 'Aplicando…'
                      : 'Aplicar a todas las líneas'}
                  </Button>
                  {/* Spelled out because it surprises people: it is the only honest
                      meaning of "apply to everything", and the alternative is a line
                      left at 12 % that is discovered from the total. */}
                  <p className="text-ink-3 w-full text-[12px] leading-4">
                    Sustituye los márgenes individuales.
                    {tariffLines ? ' Las líneas de las tarifas siguen con su descuento.' : ''}
                    {dirty ? ' Guarda las líneas antes de aplicarlo.' : ''}
                  </p>
                  {applied && !dirty ? (
                    <Callout compact tone="neutral" role="status" className="w-full">
                      Margen aplicado a todas las líneas.{' '}
                      <button
                        type="button"
                        className="link"
                        onClick={() => void undoMargin()}
                        disabled={undoingMargin}
                      >
                        {undoingMargin ? 'Deshaciendo…' : 'Deshacer'}
                      </button>
                    </Callout>
                  ) : null}
                  {recalculate.isError ? (
                    <ErrorMessage error={recalculate.error} className="w-full">
                      No se pudo aplicar el margen
                    </ErrorMessage>
                  ) : null}
                </div>
              ) : undefined
            }
          />

          <Warnings quote={quote} showPurchase={showsPurchase} className="order-5" />
          {/* Contract v10.24: a draft read from an email that was not an offer (an order
              confirmation, in the demo) is corrected where it is seen. */}
          {editable && reading.email && !reading.manual ? (
            <NotAnOffer email={reading.email} className="order-7" />
          ) : null}

          {offerCard ? (
            <Card
              title="La oferta del proveedor"
              className={offerFirst ? 'order-3' : 'order-6'}
              description={
                !quote.email_id
                  ? 'Súbela y el sistema la lee y rellena las líneas. O tecléalas tú.'
                  : undefined
              }
            >
              {!quote.email_id && editable ? (
                <OfferUpload
                  onUpload={(files) => upload.mutate(files)}
                  pending={upload.isPending}
                  error={upload.isError ? upload.error : null}
                />
              ) : null}
              <OfferReadingStatus quote={quote} state={reading.state} email={reading.email} />
            </Card>
          ) : showsPurchase && quote.supplier_name && offerDetails ? (
            <details className="group order-6 text-[13.5px]">
              <summary className="text-ink-3 hover:text-ink inline-flex cursor-pointer list-none items-center gap-1.5 font-medium select-none [&::-webkit-details-marker]:hidden">
                Datos de la oferta
                <span aria-hidden="true" className="text-ink-4 text-[10px] group-open:rotate-180">
                  ▾
                </span>
              </summary>
              <dl className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-[13rem_minmax(0,1fr)]">
                <dt className="text-ink-3">Proveedor</dt>
                <dd className="text-ink">{quote.supplier_name}</dd>
                {quote.supplier_offer_number ? (
                  <>
                    <dt className="text-ink-3">Nº de oferta</dt>
                    <dd className="text-ink">{quote.supplier_offer_number}</dd>
                  </>
                ) : null}
                {quote.supplier_offer_date ? (
                  <>
                    <dt className="text-ink-3">Fecha de la oferta</dt>
                    <dd className="text-ink">
                      {formatDate(dateFormat, quote.supplier_offer_date)}
                    </dd>
                  </>
                ) : null}
                {quote.supplier_valid_until ? (
                  <>
                    {/* The supplier's validity, not the one Cubas gives the customer. A quote
                        whose cost expires before the customer answers has to be re-priced. */}
                    <dt className="text-ink-3">La oferta vale hasta</dt>
                    <dd className="text-ink">
                      {formatDate(dateFormat, quote.supplier_valid_until)}
                    </dd>
                  </>
                ) : null}
                {quote.payment_terms ? (
                  <>
                    <dt className="text-ink-3">Forma de pago</dt>
                    <dd className="text-ink">{quote.payment_terms}</dd>
                  </>
                ) : null}
              </dl>
            </details>
          ) : null}
        </div>

        <div className={COLUMN}>
          <Card title="Resumen" className="order-6 xl:order-2" bodyClassName="gap-2">
            <dl className="grid grid-cols-[1fr_max-content] gap-y-1 text-[14px]">
              <dt className="text-ink-3">Base imponible</dt>
              <dd className="num text-ink">{money(quote.total_net)}</dd>
              <dt className="text-ink-3">IVA</dt>
              <dd className="num text-ink">{money(quote.total_tax)}</dd>
              <dt className="text-ink border-line-2 border-t pt-1.5 font-semibold">Total</dt>
              <dd className="num font-display text-ink border-line-2 border-t pt-1.5 text-[22px] leading-7 font-semibold">
                {money(quote.total_gross)}
              </dd>
            </dl>
            <p className="text-ink-3 text-[12px] leading-4">
              {showsPurchase ? `Margen por defecto ${percent(quote.default_margin_pct)}.` : null}
              {dirty ? ' Los importes se actualizarán al guardar las líneas.' : ''}
            </p>
          </Card>

          <div ref={clientRef} className="order-1 scroll-mt-6">
            <Card title="Cliente">
              {editable ? (
                <>
                  <ClientPicker
                    selected={client.data ?? null}
                    loading={clientLoading}
                    error={client.isError ? client.error : null}
                    onSelect={(chosen) => updateQuote.mutate({ client_id: chosen?.id ?? null })}
                  />
                  {updateQuote.isError ? (
                    <ErrorMessage error={updateQuote.error}>
                      No se pudo cambiar el cliente
                    </ErrorMessage>
                  ) : null}
                </>
              ) : (
                <FrozenClient quote={quote} />
              )}
            </Card>
          </div>

          <QuoteActions
            quote={quote}
            blocked={blocked}
            dirty={dirty}
            onSaveLines={() => linesRef.current?.save() ?? Promise.resolve(false)}
            onChooseClient={chooseClient}
            className="order-7"
          />
          {quote.status !== 'cancelled' ? <CopiesCard quote={quote} className="order-8" /> : null}
        </div>
      </div>

      {pasting ? <PasteList quote={quote} onClose={() => setPasting(false)} /> : null}
      {fromTariff ? null : <AiNotice className="mt-1" />}
      {/* Keep the last fields above the fixed action bar, including wrapped mobile text. */}
      {quote.status !== 'cancelled' ? (
        <div aria-hidden="true" className="h-44 shrink-0 sm:h-28" />
      ) : null}
    </>
  );
}

/**
 * What the reading of an uploaded document is doing, told to the person waiting for it.
 *
 * `reading` is a spinner with a promise of how long; `retrying` says the platform is
 * asking again on its own and when (contract v10.31), with no red; `failed` is what
 * failed in a sentence and its code, and the same Reprocesar the inbox has; `unread` is the rarer "it finished and read
 * nothing", with the same way out. `read` says nothing here: the lines are the message,
 * and when there are none the note the back left is in the warnings card.
 */
function OfferReadingStatus({
  quote,
  state,
  email,
}: {
  quote: Quote;
  state: ReturnType<typeof useOfferReading>['state'];
  email: ReturnType<typeof useOfferReading>['email'];
}) {
  const reprocess = useReprocess(quote.email_id ?? '');
  // A tick for the "lleva N s" line. Only while reading: the interval is cleared the
  // moment the state moves on, and it never runs on a quote with nothing in flight.
  // The tick only forces a render; the time itself is read from the clock at render,
  // so a throttled timer (background tab) makes the line update late, never wrong.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (state !== 'reading') return;
    const timer = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, [state]);
  const retry = (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => reprocess.mutate()}
      disabled={reprocess.isPending}
    >
      {reprocess.isPending ? 'Encolando…' : 'Volver a intentarlo'}
    </Button>
  );
  if (state === 'none' || state === 'read') return null;
  if (state === 'reading') {
    const progress = readingProgress(email, Date.now());
    const names = email?.attachments?.map((attachment) => attachment.filename) ?? [];
    return (
      <Callout
        tone={progress.late ? 'warn' : 'info'}
        role="status"
        action={progress.late ? retry : undefined}
      >
        <span className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="border-line border-t-accent size-4 shrink-0 animate-spin rounded-full border-2"
          />
          <span>
            {progress.late
              ? 'La lectura está tardando más de lo previsto. '
              : 'Leyendo el documento. '}
            {readingEstimateText(names)} Las líneas aparecerán aquí solas.
            {email ? (
              <span className="text-ink-3 ml-1">
                Lleva {progress.elapsedSeconds} s
                {progress.late ? '' : ` (previstos unos ${progress.estimateSeconds} s)`}.
              </span>
            ) : null}
            {progress.late ? (
              <span className="mt-1 block">
                Puede que el proveedor esté lento. Si en unos minutos no aparece nada, «Volver a
                intentarlo» lo manda a leer otra vez; no cuesta dos veces si la primera lectura
                termina.
              </span>
            ) : null}
          </span>
        </span>
      </Callout>
    );
  }
  // The code of what failed (contract v10.31): the email's own for an upload left in
  // `error`, the reading's for an offer classified without a draft. Never the raw text,
  // which only a superadministrator gets, under «Detalles técnicos».
  const code = email?.error_code ?? email?.reading_error_code ?? email?.auto_retry?.code ?? null;
  if (state === 'retrying' && email?.auto_retry) {
    return (
      <div className="flex flex-col gap-2">
        <Callout
          tone="info"
          role="status"
          title="Reintentando la lectura"
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => reprocess.mutate()}
              disabled={reprocess.isPending}
            >
              {reprocess.isPending ? 'Encolando…' : 'Intentarlo ahora'}
            </Button>
          }
        >
          {failureText(code)} {retryLine(email.auto_retry)} Las líneas aparecerán aquí solas; no
          hace falta que hagas nada.
          <FailureCode code={code} />
        </Callout>
        {reprocess.isError ? (
          <ErrorMessage error={reprocess.error}>No se pudo volver a leer</ErrorMessage>
        ) : null}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {state === 'failed' ? (
        <Callout tone="danger" title="No se pudo leer el documento" action={retry}>
          {failureText(code)}{' '}
          {email?.auto_retry?.exhausted ? `${retryLine(email.auto_retry)} ` : null}
          {failurePasses(code)
            ? '«Volver a intentarlo» lo manda a leer otra vez. Si sigue fallando, teclea las líneas a mano o avisa a quien administra la aplicación con este código.'
            : 'Teclea las líneas a mano o sube el documento de otra forma, y si no sabes por qué, avisa a quien administra la aplicación con este código.'}
          <FailureCode code={code} />
          <TechnicalDetails text={email?.error} />
        </Callout>
      ) : (
        <Callout tone="warn" title="El documento no llegó a leerse" action={retry}>
          Se encoló pero no salió ninguna lectura. Suele ser el límite diario del proveedor:
          inténtalo otra vez, o teclea las líneas a mano.
        </Callout>
      )}
      {reprocess.isError ? (
        <ErrorMessage error={reprocess.error}>No se pudo volver a leer</ErrorMessage>
      ) : null}
    </div>
  );
}

/**
 * The customer as the document carries it, once the quote has been approved.
 *
 * Read from the quote's own `client_*` columns and **not** from `clients`, which is the
 * whole point of the snapshot: this is what the PDF printed, and a customer who moves next
 * month does not get to change a document that was already sent. If it ever disagrees with
 * the client card, the card is the one that moved.
 */
function FrozenClient({ quote }: { quote: Quote }) {
  if (!quote.client_name) {
    return (
      <p className="text-ink-2 text-[14px]">
        Se aprobó sin cliente: el documento lleva el bloque del cliente en blanco.
      </p>
    );
  }

  const locality = [quote.client_postal_code, quote.client_city].filter(Boolean).join(' ');
  const lines = [quote.client_street, locality, quote.client_province].filter(
    (line): line is string => Boolean(line && line.trim()),
  );

  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-ink text-[15px] font-semibold">{quote.client_name}</p>
      <p className="text-ink-3 text-[13px]">
        {[quote.client_code, quote.client_nif].filter(Boolean).join(' · ') || 'Sin código ni NIF'}
      </p>
      {lines.map((line) => (
        <p key={line} className="text-ink-2 text-[13.5px]">
          {line}
        </p>
      ))}
      {quote.client_payment_terms ? (
        <p className="text-ink-2 mt-1 text-[13.5px]">Pago: {quote.client_payment_terms}</p>
      ) : null}
      <p className="text-ink-3 mt-2 text-[12px] leading-4">
        Congelado al aprobar: es lo que imprime el PDF, cambie luego lo que cambie la ficha.
      </p>
    </div>
  );
}
